import { useCallback, useMemo, useRef } from 'react'
import { isAbortError, isTimeoutError } from 'api/requestUtils'
import { reportLog } from 'services/userMonitoring'
import uuidV4 from 'lib/string/uuidV4Utils'
import stableStringifyForCacheKey from 'lib/object/stableStringifyForCacheKey'
import { sha256 } from 'lib/utils/createHashUtils'
import {
  ASSISTANT_SURFACE_SNAPSHOT_ENSURE_TIMEOUT_MS,
  ensureAssistantSurfaceSnapshotRef,
  isSnapshotAuthResponse,
  isSnapshotChunkCacheMissResponse,
  isSnapshotIdempotencyConflictResponse,
  isSnapshotStoreUnavailableResponse,
  type EnsureAssistantSurfaceSnapshotRefRequest,
} from 'tripPlanner/api/v3'
import { isNetworkError } from 'tripPlanner/utils/networkError'
import {
  assistantSurfaceSnapshotLatencyBucket,
  trackAssistantSurfaceSnapshotOutcome,
  type AssistantSurfaceSnapshotOutcome,
} from 'tripPlanner/utils/chatAnalytics'
import type {
  AssistantSurfaceSnapshotChunk,
  AssistantSurfaceSnapshotChunkManifest,
  AssistantSurfaceSnapshotRef,
  AssistantSurfaceSnapshotRequest,
} from 'tripPlanner/assistant/api/surfaceSnapshotContract'
import type { AssistantSurfaceId } from 'tripPlanner/assistant/api/types'
import type { SurfaceSnapshotAuthorityFields } from 'tripPlanner/assistant/surfaceFocus/resolveFocusAction'

export interface AssistantSurfaceSnapshotBuildResult {
  chunks: Array<AssistantSurfaceSnapshotChunk>
  manifest: AssistantSurfaceSnapshotChunkManifest
  snapshot: AssistantSurfaceSnapshotRequest
}

interface LiveSnapshotRef {
  chunkHashes: Map<string, string>
  contentHash: string
  ref: AssistantSurfaceSnapshotRef
}

export type AssistantSurfaceSnapshotAuthority =
  | {
    kind: 'snapshot_ref'
    snapshotRef: AssistantSurfaceSnapshotRef
  }
  | {
    kind: 'degraded'
  }

function isLive(ref: AssistantSurfaceSnapshotRef): boolean {
  return Date.parse(ref.expiresAt) > Date.now()
}

function chunkIdentity(kind: string, chunkId: string): string {
  return `${kind}:${chunkId}`
}

function liveRefIdentity(chatId: number, surfaceContextScopeKey: string): string {
  return `${chatId}:${surfaceContextScopeKey}`
}

function remainingEnsureDeadline(deadline: number): number | undefined {
  const remaining = deadline - Date.now()
  return remaining > 0 ? remaining : undefined
}

/**
 * Classifies an ensure failure for analytics only — every branch here still
 * degrades identically. `fallbackOutcome` lets a failure with no specific
 * auth/idempotency/network shape keep a caller-appropriate residual bucket —
 * e.g. a full-chunk recovery attempt that fails for an unclassified reason is
 * still a cache-miss-recovery failure, not a generic validation failure.
 */
export function classifyEnsureFailureOutcome(
  error: unknown,
  fallbackOutcome: AssistantSurfaceSnapshotOutcome = 'degraded_validation',
): AssistantSurfaceSnapshotOutcome {
  if (isSnapshotAuthResponse(error)) {
    return 'degraded_auth'
  }

  if (isSnapshotIdempotencyConflictResponse(error)) {
    return 'degraded_idempotency'
  }

  if (isSnapshotStoreUnavailableResponse(error) || isNetworkError(error)) {
    return 'degraded_network'
  }

  // A deadline-exhausted-before-request branch already reports 'degraded_timeout'
  // directly (see the two callers above); this catches the request itself timing
  // out mid-flight — the fetch rejects with a TimeoutError, which isNetworkError's
  // message-substring check doesn't match, so without this it fell through to the
  // generic validation-failure bucket.
  if (isTimeoutError(error)) {
    return 'degraded_timeout'
  }

  return fallbackOutcome
}

export function useAssistantSurfaceSnapshot(surface: AssistantSurfaceId) {
  const liveRefsRef = useRef(new Map<string, LiveSnapshotRef>())
  // Keyed by chatId alone (the focus executor knows the chat but is discovering the *current*
  // scope key, so it can't use the composite `liveRefsRef` key). Last-resolve-wins, which is
  // the most recently accepted authority for that chat.
  const latestByChatIdRef = useRef(new Map<number, AssistantSurfaceSnapshotRef>())
  // Tracks the non-reused token of the most recently *started* resolve call per chatId, so a call
  // that started earlier but resolves later can detect it's been superseded and skip writing a
  // stale authority over a newer one -- without this, two overlapping resolves for the same chat
  // (a rapid resend/regenerate before the first settles) could let the later-finishing-but-
  // earlier-started call stomp a newer authority with a stale one.
  const latestCallTokenByChatIdRef = useRef(new Map<number, symbol>())

  const clearAssistantSurfaceSnapshotRefs = useCallback(() => {
    liveRefsRef.current.clear()
    latestByChatIdRef.current.clear()
    latestCallTokenByChatIdRef.current.clear()
  }, [])

  const getLiveSurfaceSnapshotAuthority = useCallback((
    chatId: number,
  ): SurfaceSnapshotAuthorityFields | undefined => {
    const ref = latestByChatIdRef.current.get(chatId)
    // Re-apply the expiry check at read time — an expired entry is exactly the stale case the
    // executor's staleness gate exists to reject, so never hand one back.
    if (!ref || !isLive(ref)) {
      return undefined
    }

    return {
      snapshotRefId: ref.snapshotRefId,
      surface: ref.surface,
      surfaceContextScopeKey: ref.surfaceContextScopeKey,
      snapshotFingerprint: ref.snapshotFingerprint,
    }
  }, [])

  const resolveSurfaceSnapshotAuthority = useCallback(async(
    chatId: number,
    snapshot: AssistantSurfaceSnapshotBuildResult | undefined,
    accessToken: string | undefined,
    signal?: AbortSignal,
  ): Promise<AssistantSurfaceSnapshotAuthority> => {
    const startedAt = Date.now()
    const track = (outcome: AssistantSurfaceSnapshotOutcome) =>
      trackAssistantSurfaceSnapshotOutcome(surface, outcome, assistantSurfaceSnapshotLatencyBucket(Date.now() - startedAt))

    const callToken = Symbol('surface snapshot resolve')
    latestCallTokenByChatIdRef.current.set(chatId, callToken)
    const isLatestCall = () => latestCallTokenByChatIdRef.current.get(chatId) === callToken
    const setLatestAuthorityIfNotSuperseded = (ref: AssistantSurfaceSnapshotRef) => {
      if (isLatestCall()) {
        latestByChatIdRef.current.set(chatId, ref)
      }
    }
    const setLiveRefIfNotSuperseded = (refIdentity: string, entry: LiveSnapshotRef) => {
      if (isLatestCall()) {
        liveRefsRef.current.set(refIdentity, entry)
      }
    }

    if (!snapshot) {
      // No snapshot to publish — either zero/2+ sources registered (the same
      // invalid-registry invariant useStreamConnection fails closed on) or the
      // surface adapter's own build attempt returned nothing. Both collapse to
      // the same degraded/validation outcome; tracked so a regression that
      // makes either case reachable would actually show up.
      track('degraded_validation')
      return { kind: 'degraded' }
    }

    try {
      const contentHash = sha256(stableStringifyForCacheKey(snapshot.manifest))

      const refIdentity = liveRefIdentity(chatId, snapshot.manifest.surfaceContextScopeKey)
      const existing = liveRefsRef.current.get(refIdentity)
      // An expired entry must be treated as "no existing entry" for both the
      // cache-hit check and the changedChunks diff below — otherwise diffing
      // against a stale-but-content-matching entry produces an empty
      // changedChunks set the backend can't satisfy, forcing an avoidable
      // extra round trip through the cache-miss recovery path.
      const liveExisting = existing && isLive(existing.ref) ? existing : undefined
      if (liveExisting?.contentHash === contentHash) {
        setLatestAuthorityIfNotSuperseded(liveExisting.ref)
        return { kind: 'snapshot_ref', snapshotRef: liveExisting.ref }
      }

      const idempotencyKey = uuidV4()
      const changedChunks = liveExisting ?
          snapshot.chunks.filter(chunk => liveExisting.chunkHashes.get(chunkIdentity(chunk.kind, chunk.chunkId)) !== chunk.contentHash) :
        snapshot.chunks
      const request: EnsureAssistantSurfaceSnapshotRefRequest = {
        mode: 'chunked',
        chatId,
        manifest: snapshot.manifest,
        changedChunks,
        idempotencyKey,
      }
      const deadline = Date.now() + ASSISTANT_SURFACE_SNAPSHOT_ENSURE_TIMEOUT_MS

      try {
        const timeoutMs = remainingEnsureDeadline(deadline)
        if (timeoutMs === undefined) {
          track('degraded_timeout')
          reportLog('[AssistantSurfaceSnapshot] Ensure deadline exhausted before publish; falling back to degraded identity', {})
          return { kind: 'degraded' }
        }

        const response = await ensureAssistantSurfaceSnapshotRef(request, accessToken, timeoutMs, signal)
        setLiveRefIfNotSuperseded(refIdentity, {
          chunkHashes: new Map(snapshot.manifest.chunks.map(chunk => [chunkIdentity(chunk.kind, chunk.chunkId), chunk.contentHash])),
          contentHash,
          ref: response.snapshotRef,
        })
        setLatestAuthorityIfNotSuperseded(response.snapshotRef)
        track('chunked_resolved')
        return { kind: 'snapshot_ref', snapshotRef: response.snapshotRef }
      } catch(error) {
        // A caller-triggered abort (e.g. Stop clicked mid-ensure) is expected, not a
        // failure — degrade silently rather than logging it as one, and not a
        // lifecycle resolution worth tracking either.
        if (isAbortError(error)) {
          return { kind: 'degraded' }
        }

        // Only typed chunk-cache miss may recover with a full-chunk republish. Store
        // unavailable (`503 snapshot_store_unavailable`), idempotency conflict
        // (`409 snapshot_idempotency_conflict`), and all other ensure failures degrade silently.
        if (!isSnapshotChunkCacheMissResponse(error)) {
          track(classifyEnsureFailureOutcome(error))
          reportLog('[AssistantSurfaceSnapshot] Failed to ensure snapshot ref; falling back to degraded identity', { error })
          return { kind: 'degraded' }
        }

        try {
          const timeoutMs = remainingEnsureDeadline(deadline)
          if (timeoutMs === undefined) {
            track('degraded_timeout')
            reportLog('[AssistantSurfaceSnapshot] Ensure deadline exhausted before full-chunk recovery; falling back to degraded identity', {})
            return { kind: 'degraded' }
          }

          const allChunksRequest: EnsureAssistantSurfaceSnapshotRefRequest = {
            mode: 'chunked',
            chatId,
            manifest: snapshot.manifest,
            changedChunks: snapshot.chunks,
            idempotencyKey,
          }
          const response = await ensureAssistantSurfaceSnapshotRef(allChunksRequest, accessToken, timeoutMs, signal)
          setLiveRefIfNotSuperseded(refIdentity, {
            chunkHashes: new Map(snapshot.manifest.chunks.map(chunk => [chunkIdentity(chunk.kind, chunk.chunkId), chunk.contentHash])),
            contentHash,
            ref: response.snapshotRef,
          })
          setLatestAuthorityIfNotSuperseded(response.snapshotRef)
          track('chunked_recovery_resolved')
          return { kind: 'snapshot_ref', snapshotRef: response.snapshotRef }
        } catch(recoveryError) {
          if (isAbortError(recoveryError)) {
            return { kind: 'degraded' }
          }
          track(classifyEnsureFailureOutcome(recoveryError, 'degraded_chunk_cache_miss'))
          reportLog('[AssistantSurfaceSnapshot] Full-chunk recovery after cache miss failed; falling back to degraded identity', { error: recoveryError })
          return { kind: 'degraded' }
        }
      }
    } catch(error) {
      if (isAbortError(error)) {
        return { kind: 'degraded' }
      }
      track('degraded_validation')
      reportLog('[AssistantSurfaceSnapshot] Failed to publish snapshot; falling back to degraded identity', { error })
      return { kind: 'degraded' }
    }
  }, [surface])

  return useMemo(() => ({
    clearAssistantSurfaceSnapshotRefs,
    getLiveSurfaceSnapshotAuthority,
    resolveSurfaceSnapshotAuthority,
  }), [clearAssistantSurfaceSnapshotRefs, getLiveSurfaceSnapshotAuthority, resolveSurfaceSnapshotAuthority])
}
